/**
 * weeklySlotFormatter — un créneau de la semaine (« le jeudi à 18 h, toutes les semaines ») dit à
 * l'écran : « Jeudi · 18h00 » sur une ligne, « Jeu » en tête d'une rangée de la grille, « 18h00 »
 * sur une case.
 *
 * Un créneau arrive écrit deux fois par le serveur : `label` (« jeudi 18h », « jeudi 18h30 »), à
 * l'heure de la famille, et la clé (« Thursday 18:00 »), à l'heure du serveur. Le libellé fait foi —
 * c'est l'heure que la famille vit ; la clé ne sert que de repli quand il manque ou ne se lit pas.
 * Rien n'est converti ici : le serveur a déjà fait le fuseau.
 */
import { capitalizeFirst } from "./textFormatter"

/** Lundi d'abord : la semaine d'un parent commence le lundi. */
const FRENCH_DAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"]
const ENGLISH_DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
/** « Lun », « Mar »… : les trois premières lettres du jour. */
const SHORT_DAYS = FRENCH_DAYS.map((day) => capitalizeFirst(day.slice(0, 3)))

/** Un créneau décomposé : le jour (0 = lundi), son nom, l'heure et les minutes. */
export type WeeklySlotParts = { weekday: number; day: string; hours: number; minutes: number }

/** « jeudi 18h » · « jeudi 18h30 » → ses morceaux ; `null` s'il ne se lit pas. */
function fromLabel(label: string | null | undefined): WeeklySlotParts | null {
    const match = /^\s*(\S+)\s+(\d{1,2})h(\d{2})?\s*$/i.exec(label ?? "")
    if (match == null) return null
    const weekday = FRENCH_DAYS.indexOf(match[1]!.toLowerCase())
    if (weekday < 0) return null
    return { weekday, day: FRENCH_DAYS[weekday]!, hours: Number(match[2]), minutes: Number(match[3] ?? 0) }
}

/** « Thursday 18:00 » → ses morceaux ; `null` s'il ne se lit pas. */
function fromKey(key: string | null | undefined): WeeklySlotParts | null {
    const match = /^\s*(\S+)\s+(\d{1,2}):(\d{2})/.exec(key ?? "")
    if (match == null) return null
    const weekday = ENGLISH_DAYS.indexOf(match[1]!.toLowerCase())
    if (weekday < 0) return null
    return { weekday, day: FRENCH_DAYS[weekday]!, hours: Number(match[2]), minutes: Number(match[3]) }
}

/** Les morceaux d'un créneau : le libellé de la famille d'abord, la clé du serveur à défaut. */
export function weeklySlotParts(label: string | null | undefined, key?: string | null): WeeklySlotParts | null {
    return fromLabel(label) ?? fromKey(key)
}

/**
 * L'heure comme on l'écrit à un parent — « 18h00 », « 18h30 ». `compact` la raccourcit pour une
 * case de grille, où l'heure est seule sous son jour — « 18h », « 18h30 ».
 */
export function formatSlotHour(parts: Pick<WeeklySlotParts, "hours" | "minutes">, compact = false): string {
    if (compact) return `${parts.hours}h${parts.minutes === 0 ? "" : String(parts.minutes).padStart(2, "0")}`
    return `${String(parts.hours).padStart(2, "0")}h${String(parts.minutes).padStart(2, "0")}`
}

/**
 * Un créneau sur une ligne — « Jeudi · 18h00 » (`form` `"label"`, défaut) — ou dans une phrase —
 * « jeudi 18h00 » (`"sentence"`). Un créneau qui ne se lit pas rend son libellé tel quel.
 */
export function formatWeeklySlot(
    label: string | null | undefined,
    key?: string | null,
    form: "label" | "sentence" = "label"
): string {
    const parts = weeklySlotParts(label, key)
    if (parts == null) return label ?? key ?? ""
    return form === "sentence"
        ? `${parts.day} ${formatSlotHour(parts)}`
        : `${capitalizeFirst(parts.day)} · ${formatSlotHour(parts)}`
}

/** « Lun » … « Dim » — l'en-tête d'une rangée de la grille ; « » hors de la semaine. */
export function formatShortWeekday(weekday: number): string {
    return SHORT_DAYS[weekday] ?? ""
}

/** « Monday » … « Sunday » — l'identifiant d'un jour (0 = lundi). */
export function weekdayKey(weekday: number): string {
    return capitalizeFirst(ENGLISH_DAYS[weekday] ?? "")
}
